/*
 * Site-wide JavaScript, loaded on every page from layout/theme.liquid.
 */

/*
 * Keep your place when pressing "back".
 * Browsers try to do this themselves, but on phones (especially iPhone Safari, slow connections
 * and pop-ups that load late) they often end up at the top. So we remember how far down each
 * page you were, and when you come back we scroll there - and keep you there for a few seconds
 * while images and pop-ups load, unless you start scrolling yourself.
 */
(() => {
  const key = `sunday:scroll:${location.pathname}${location.search}`;
  const save = () => {
    try {
      sessionStorage.setItem(key, String(Math.round(window.scrollY)));
    } catch {}
  };
  const saved = () => {
    try {
      return Number(sessionStorage.getItem(key)) || 0;
    } catch {
      return 0;
    }
  };

  const navigation = performance.getEntriesByType('navigation')[0];
  const cameBack = navigation && navigation.type === 'back_forward';
  const backTarget = cameBack ? saved() : 0;

  // Save as you scroll (iPhones don't always report leaving the page), and when you leave.
  let saveTimer;
  window.addEventListener('scroll', () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 150);
  }, { passive: true });
  window.addEventListener('pagehide', save);
  document.addEventListener('click', (event) => {
    if (event.target.closest('a[href]')) save();
  }, true);

  // Bring you back to `target`, holding it for a few seconds against late-loading content.
  // Only real scrolling stops it - a tap, or the iPhone swipe-back gesture, doesn't count.
  const holdAt = (target) => {
    if (target <= 0) return;
    history.scrollRestoration = 'manual';
    let userScrolled = false;
    const stop = () => (userScrolled = true);
    ['wheel', 'touchmove', 'keydown'].forEach((type) =>
      window.addEventListener(type, stop, { once: true, passive: true })
    );

    const until = Date.now() + 3000;
    const step = () => {
      if (userScrolled) return;
      if (Math.abs(window.scrollY - target) > 2) window.scrollTo(0, target);
      if (Date.now() < until) setTimeout(step, 50);
    };
    step();
    window.addEventListener('load', () => !userScrolled && window.scrollTo(0, target), { once: true });
  };

  // Came back with the back/forward button and the page loaded fresh
  if (cameBack) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => holdAt(backTarget), { once: true });
    } else {
      holdAt(backTarget);
    }
  }

  // Came back to a page the browser kept in memory (common on iPhone): it should already be in
  // place, but if it landed at the top, put it back.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && window.scrollY < 10) holdAt(saved());
  });
})();

// Any part of the site can fire "cart:updated" (product page, drawer, cart page...);
// this refreshes the header's cart count to match.
document.addEventListener('cart:updated', async () => {
  const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count > 0 ? ` (${cart.item_count})` : '';
  });
  if (cart.item_count === 0) sessionStorage.removeItem(CART_TIMER_KEY);
});

/*
 * Product card colour swatches (snippets/product-card.liquid).
 * Clicking a swatch swaps the card's photo (and hover photo) and points its links
 * at that colour. Everything needed is already on the button, so nothing is loaded.
 */
document.addEventListener('click', (event) => {
  const swatch = event.target.closest('[data-swatch]');
  if (!swatch) return;

  const card = swatch.closest('[data-product-card]');
  const image = card.querySelector('[data-card-image]');
  const hover = card.querySelector('[data-card-hover]');

  if (image && swatch.dataset.srcset) {
    image.srcset = swatch.dataset.srcset;
    image.src = swatch.dataset.srcset.split(' ')[0];
  }

  if (hover) {
    if (swatch.dataset.hoverSrcset) {
      hover.srcset = swatch.dataset.hoverSrcset;
      hover.src = swatch.dataset.hoverSrcset.split(' ')[0];
      hover.sizes = image?.sizes || '';
      hover.hidden = false;
    } else {
      hover.hidden = true; // this colour has no second photo
    }
  }

  card.querySelectorAll('[data-card-link]').forEach((link) => {
    link.href = swatch.dataset.url;
  });

  card.querySelectorAll('[data-swatch]').forEach((s) => {
    const active = s === swatch;
    s.classList.toggle('is-active', active);
    s.setAttribute('aria-pressed', String(active));
  });
});

/*
 * "your cart is reserved for 02:59" countdown (snippets/cart-timer.liquid).
 * The end time is saved in sessionStorage when something is first added, so the
 * clock keeps counting between pages. Visual only: it doesn't actually hold stock.
 */
const CART_TIMER_KEY = 'sunday:cart-timer-end';

document.addEventListener('cart:added', () => {
  const end = Number(sessionStorage.getItem(CART_TIMER_KEY));
  if (end > Date.now()) return; // already counting down
  const minutes = Number(window.theme?.cartTimerMinutes) || 3;
  sessionStorage.setItem(CART_TIMER_KEY, String(Date.now() + minutes * 60 * 1000));
});

class CartTimer extends HTMLElement {
  connectedCallback() {
    this.update();
    this.interval = setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  update() {
    const remaining = Number(sessionStorage.getItem(CART_TIMER_KEY)) - Date.now();
    if (!remaining || remaining <= 0) {
      this.hidden = true; // ran out, or never started
      return;
    }
    const totalSeconds = Math.ceil(remaining / 1000);
    const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const ss = String(totalSeconds % 60).padStart(2, '0');
    this.querySelector('[data-timer-clock]').textContent = `${mm}:${ss}`;
    this.hidden = false;
  }
}

customElements.define('cart-timer', CartTimer);

/*
 * Currency selector: a dropdown is normally as wide as its longest option
 * ("united kingdom | gbp £"), leaving a gap before "cart". Size it to the option showing.
 * Newer browsers do this with CSS (field-sizing: content); this covers the rest.
 */
document.querySelectorAll('.header__localization select').forEach((select) => {
  if (CSS.supports('field-sizing', 'content')) return;
  const fit = () => {
    const probe = document.createElement('span');
    const style = getComputedStyle(select);
    probe.textContent = select.options[select.selectedIndex]?.text.trim() || '';
    Object.assign(probe.style, { position: 'absolute', visibility: 'hidden', whiteSpace: 'pre', font: style.font, letterSpacing: style.letterSpacing, textTransform: style.textTransform });
    document.body.append(probe);
    select.style.width = `${Math.ceil(probe.getBoundingClientRect().width) + 2}px`;
    probe.remove();
  };
  fit();
  document.fonts?.ready.then(fit);
});

/*
 * Smooth open/close for fold-out rows and dropdowns (<details> elements):
 * product page panels, FAQs, T&C sections, size charts, mobile menu dropdowns.
 * Instead of snapping, the row slides to its new height and the contents fade.
 * The mobile menu ("directory") fades/slides in and out as a whole.
 * People who've asked their device for reduced motion get the instant version.
 */
(() => {
  const ROWS = '.accordion, .foldout, .foldout-group, .size-chart, .drawer-menu details, .password-page__enter';
  const MENU = '.header__drawer';
  const DURATION = 260;
  const EASING = 'cubic-bezier(0.25, 0.8, 0.3, 1)';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Height of a <details> when closed = its summary + its own padding/border
  const closedHeight = (details, summary) => {
    const s = getComputedStyle(details);
    const extra = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
      .reduce((sum, prop) => sum + parseFloat(s[prop]), 0);
    return summary.offsetHeight + extra;
  };

  const toggleRow = (details, summary) => {
    // Clicked again mid-close? Then reopen it (it's still technically "open" until the close finishes)
    const opening = !details.open || details.classList.contains('is-closing');
    details._animation?.cancel();
    const startHeight = details.offsetHeight;
    details.classList.toggle('is-closing', !opening); // lets arrows turn straight away (see CSS)
    if (opening) details.open = true;
    const endHeight = opening ? details.offsetHeight : closedHeight(details, summary);

    details.style.overflow = 'hidden';
    const content = [...details.children].filter((el) => el !== summary);
    content.forEach((el) =>
      el.animate([{ opacity: opening ? 0 : 1 }, { opacity: opening ? 1 : 0 }], { duration: DURATION, easing: EASING })
    );
    const animation = details.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: DURATION, easing: EASING }
    );
    details._animation = animation;
    animation.onfinish = () => {
      if (!opening) details.open = false;
      details.classList.remove('is-closing');
      details.style.overflow = '';
      details._animation = null;
    };
    animation.oncancel = () => {
      details.style.overflow = '';
      details.classList.remove('is-closing');
    };
  };

  const toggleMenu = (details) => {
    const panel = details.querySelector('.header__drawer-panel');
    if (!panel) return;
    const opening = !details.open;
    if (opening) details.open = true;
    const frames = [
      { opacity: 0, transform: 'translateY(-8px)' },
      { opacity: 1, transform: 'translateY(0)' },
    ];
    const animation = panel.animate(opening ? frames : frames.reverse(), { duration: 220, easing: EASING });
    if (!opening) animation.onfinish = () => (details.open = false);
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('summary');
    if (!summary || reduceMotion.matches) return;
    const details = summary.parentElement;
    if (details.matches(MENU)) {
      event.preventDefault();
      toggleMenu(details);
    } else if (details.matches(ROWS)) {
      event.preventDefault();
      toggleRow(details, summary);
    }
  });
})();
